import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

/* ── Logo ── */
const Logo = ({ size = 32, color = "#2563eb" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100">
    <defs><filter id="lf"><feColorMatrix type="matrix" values="1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="r"/><feOffset in="r" dx="-1.5" result="rO"/><feColorMatrix in="SourceGraphic" type="matrix" values="0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 0 1 0" result="g"/><feOffset in="g" dx="1.5" result="gO"/><feMerge><feMergeNode in="rO"/><feMergeNode in="gO"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
    <g filter="url(#lf)" stroke={color} fill="none" strokeLinecap="round">
      <path d="M50 18 A32 32 0 0 1 82 50" strokeWidth="5"/><path d="M50 82 A32 32 0 0 1 18 50" strokeWidth="5"/>
      <line x1="14" y1="50" x2="86" y2="50" strokeWidth="4"/><line x1="50" y1="14" x2="50" y2="86" strokeWidth="4"/>
      <circle cx="14" cy="50" r="4" fill={color} stroke="none"/><circle cx="86" cy="50" r="4" fill={color} stroke="none"/>
      <circle cx="50" cy="14" r="4" fill={color} stroke="none"/><circle cx="50" cy="86" r="4" fill={color} stroke="none"/>
      <polygon points="50,40 43,56 57,56" fill={color} stroke="none"/>
      <circle cx="74" cy="26" r="4" fill={color} stroke="none"/><circle cx="26" cy="74" r="4" fill={color} stroke="none"/>
    </g>
  </svg>
);

/* ── Hero slides — each has its own headline, sub, tag, image set ── */
const HERO_SLIDES = [
  {
    tag:    "Indian Fashion",
    h1a:    "Create",
    h1b:    "stunning",
    h1c:    "saree looks.",
    sub:    "Generate professional Indian fashion portraits — bridal, ethnic, and contemporary — in seconds with AI.",
    prompt: "Indian fashion editorial, studio bokeh, blue saree",
    img0:   "https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=500&q=85",
    img1:   "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&q=85",
    img2:   "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=500&q=85",
    accent: "#2563eb",
  },
  {
    tag:    "Product Photography",
    h1a:    "Turn products",
    h1b:    "into ads",
    h1c:    "that sell.",
    sub:    "Studio-quality product photos without a studio. Marble backgrounds, golden surfaces, lifestyle scenes — all from a single upload.",
    prompt: "Luxury product on marble, soft studio lighting, 8k",
    img0:   "https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=500&q=85",
    img1:   "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=500&q=85",
    img2:   "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&q=85",
    accent: "#0ea5e9",
  },
  {
    tag:    "Food Photography",
    h1a:    "Make food",
    h1b:    "look",
    h1c:    "irresistible.",
    sub:    "Professional food photography for restaurants, brands, and menus. Styled plating, perfect lighting — zero effort.",
    prompt: "Gourmet food plating, soft natural light, restaurant editorial",
    img0:   "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=500&q=85",
    img1:   "https://images.unsplash.com/photo-1547592180-85f173990554?w=500&q=85",
    img2:   "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=500&q=85",
    accent: "#f59e0b",
  },
  {
    tag:    "Jewellery & Beauty",
    h1a:    "Showcase",
    h1b:    "jewellery",
    h1c:    "beautifully.",
    sub:    "Macro jewellery shots, beauty product flatlays, and luxury brand imagery — all generated in seconds.",
    prompt: "Gold jewellery macro photography, black velvet background",
    img0:   "https://images.unsplash.com/photo-1506630448388-4e683c67ddb0?w=500&q=85",
    img1:   "https://images.unsplash.com/photo-1583846712513-3f3623d4f37d?w=500&q=85",
    img2:   "https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=500&q=85",
    accent: "#8b5cf6",
  },
  {
    tag:    "E-commerce & Brand",
    h1a:    "Elevate your",
    h1b:    "brand",
    h1c:    "instantly.",
    sub:    "From sneaker editorials to lifestyle campaigns — create consistent, on-brand imagery at scale.",
    prompt: "Sneaker editorial, urban background, cinematic lighting",
    img0:   "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500&q=85",
    img1:   "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=500&q=85",
    img2:   "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&q=85",
    accent: "#10b981",
  },
];

const BEFORE_AFTER = [
  { before:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=340&q=80", after:"https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=340&q=80", prompt:"Change background to marble studio, add soft lighting" },
  { before:"https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=340&q=80", after:"https://images.unsplash.com/photo-1594534475808-b18fc33b045e?w=340&q=80", prompt:"Place product on golden luxury surface, cinematic" },
];

const GALLERY = [
  { url:"https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&q=80", label:"Fashion Portrait" },
  { url:"https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=400&q=80", label:"Beauty Product" },
  { url:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80", label:"Luxury Watch" },
  { url:"https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400&q=80", label:"Food Photography" },
  { url:"https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&q=80", label:"Product Shot" },
  { url:"https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&q=80", label:"Minimal Product" },
  { url:"https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400&q=80", label:"Indian Fashion" },
  { url:"https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=400&q=80", label:"Jewellery" },
  { url:"https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80", label:"Sneaker Editorial" },
  { url:"https://images.unsplash.com/photo-1526947425960-945c6e72858f?w=400&q=80", label:"Skincare Brand" },
];

const FEATURES = [
  { icon:"⚡", title:"Generate in seconds",  desc:"Nano Banana AI creates photorealistic images from plain text in under 30 seconds." },
  { icon:"🖼",  title:"Image to Image",       desc:"Upload any photo — change colors, backgrounds, or entire scenes naturally." },
  { icon:"📎", title:"Paste & Transform",    desc:"Ctrl+V to paste any image. Describe the edit and watch it happen." },
  { icon:"💡", title:"Smart Prompts",        desc:"Type naturally. AI auto-enhances your prompt for photorealistic output." },
  { icon:"⬇",  title:"HD Downloads",         desc:"Every image at full 1024×1024 resolution, ready for commercial use." },
  { icon:"🎨", title:"Any Style, Any Scene", desc:"Portraits, products, fashion, food, landscapes — infinite creativity." },
];

const STEPS = [
  { n:"01", icon:"✍", title:"Describe your image",    desc:"Type what you want in plain language — 'Indian bride gold saree studio bokeh' is enough." },
  { n:"02", icon:"🤖", title:"AI generates instantly",  desc:"Nano Banana creates photorealistic images in seconds with perfect anatomy and lighting." },
  { n:"03", icon:"⬇",  title:"Download or keep editing", desc:"Save in full HD or type a new instruction to refine endlessly." },
];

const TESTIMONIALS = [
  { q:"Pic9ine replaced my entire product photography workflow. What used to cost ₹15,000 per shoot now costs ₹399.", name:"Priya M.", role:"Saree Brand Founder, Chennai", av:"P" },
  { q:"The img2img feature is insane. I upload a plain product photo, describe the background I want, and it just works.", name:"Rahul K.", role:"E-commerce Store Owner", av:"R" },
  { q:"Every image looks like it came from a Mumbai studio. My Instagram engagement tripled since I started.", name:"Anitha S.", role:"Digital Marketing Agency", av:"A" },
];

const PACKS = [
  { n:10,  p:"₹399",   l:"Starter" },
  { n:25,  p:"₹799",   l:"Popular", hot:true },
  { n:50,  p:"₹1,299", l:"Pro" },
  { n:100, p:"₹1,999", l:"Best" },
];

/* ── CSS ── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
*,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
html{scroll-behavior:smooth;}
body{font-family:'Poppins',sans-serif;background:#fff;color:#0f172a;overflow-x:hidden;}
::-webkit-scrollbar{width:3px;}
::-webkit-scrollbar-thumb{background:linear-gradient(#2563eb,#0ea5e9);border-radius:99px;}

/* cursor */
.cur{position:fixed;width:10px;height:10px;border-radius:50%;background:#2563eb;pointer-events:none;z-index:99999;transform:translate(-50%,-50%);mix-blend-mode:difference;transition:width .12s,height .12s;}
.cur-r{position:fixed;width:36px;height:36px;border-radius:50%;border:1.5px solid rgba(37,99,235,.4);pointer-events:none;z-index:99998;transform:translate(-50%,-50%);transition:all .32s;}
.cur.big{width:5px;height:5px;}
.cur-r.big{width:52px;height:52px;border-color:rgba(37,99,235,.65);}

/* NAV */
.nav{position:fixed;inset:0 0 auto;z-index:900;height:68px;padding:0 clamp(16px,5vw,72px);display:flex;align-items:center;justify-content:space-between;background:rgba(255,255,255,.88);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-bottom:1px solid transparent;transition:border-color .3s,box-shadow .3s;}
.nav.up{border-color:rgba(37,99,235,.1);box-shadow:0 4px 32px rgba(37,99,235,.07);}
.nav-brand{display:flex;align-items:center;gap:10px;cursor:pointer;}
.nav-name{font-size:19px;font-weight:800;color:#0f172a;letter-spacing:-.5px;}
.nav-name b{color:#2563eb;}
.nav-links{display:flex;gap:32px;}
.nl{font-size:13.5px;font-weight:500;color:#64748b;cursor:pointer;background:none;border:none;font-family:'Poppins',sans-serif;transition:color .2s;padding:0;}
.nl:hover{color:#2563eb;}
.nav-r{display:flex;gap:10px;}
.btn-out{padding:8px 20px;border:1.5px solid rgba(37,99,235,.2);border-radius:99px;font-family:'Poppins',sans-serif;font-size:13px;font-weight:600;color:#2563eb;background:transparent;cursor:pointer;transition:all .2s;}
.btn-out:hover{background:#eff6ff;border-color:#2563eb;}
.btn-fill{padding:9px 22px;border:none;border-radius:99px;font-family:'Poppins',sans-serif;font-size:13px;font-weight:700;color:#fff;background:linear-gradient(135deg,#2563eb,#0ea5e9);cursor:pointer;transition:all .22s;box-shadow:0 4px 16px rgba(37,99,235,.32);}
.btn-fill:hover{transform:translateY(-1px);box-shadow:0 7px 22px rgba(37,99,235,.42);}
.nav-mob{display:none;gap:8px;}

/* ── HERO ── */
.hero{
  min-height:100vh;
  display:grid;grid-template-columns:1fr 1fr;
  align-items:center;
  padding:100px clamp(16px,5vw,72px) 60px;
  gap:48px;position:relative;overflow:hidden;background:#fff;
}
.hero-grid{position:absolute;inset:0;z-index:0;pointer-events:none;background-image:linear-gradient(rgba(37,99,235,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(37,99,235,.03) 1px,transparent 1px);background-size:56px 56px;}

/* Left side */
.hero-left{position:relative;z-index:2;}

.hero-tag{
  display:inline-flex;align-items:center;gap:8px;
  background:rgba(37,99,235,.08);border:1px solid rgba(37,99,235,.15);
  border-radius:99px;padding:6px 16px 6px 8px;
  font-size:12px;font-weight:700;color:#2563eb;
  margin-bottom:20px;transition:all .4s ease;
  opacity:0;transform:translateX(-30px);
}
.tag-ic{width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#0ea5e9);display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;flex-shrink:0;}
.tag-dot{width:6px;height:6px;border-radius:50%;background:#10b981;animation:tdot 2s ease-in-out infinite;}
@keyframes tdot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.6)}}

/* animated text lines */
.hero-h1{
  font-size:clamp(36px,4.8vw,70px);
  font-weight:900;line-height:1.06;letter-spacing:-2.5px;
  color:#0f172a;margin-bottom:20px;
  opacity:0;transform:translateY(40px);
  min-height:3.2em; /* prevents layout shift during transition */
}
.h1-a{display:block;transition:all .45s cubic-bezier(.4,0,.2,1);}
.h1-b{display:block;transition:all .45s cubic-bezier(.4,0,.2,1) .05s;
  background:linear-gradient(135deg,var(--accent,#2563eb),#0ea5e9);
  -webkit-background-clip:text;-webkit-text-fill-color:transparent;}
.h1-c{display:block;transition:all .45s cubic-bezier(.4,0,.2,1) .1s;}

.hero-sub{
  font-size:clamp(14px,1.5vw,17px);color:#475569;
  line-height:1.8;font-weight:400;max-width:480px;
  margin-bottom:32px;
  opacity:0;transform:translateY(24px);
  min-height:4em; /* prevent layout shift */
  transition:opacity .4s ease, transform .4s ease;
}

/* text change animation states */
.txt-out{opacity:0 !important;transform:translateY(-12px) !important;}
.txt-in{opacity:1;transform:translateY(0);}

.hero-btns{display:flex;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:40px;opacity:0;transform:translateY(20px);}
.btn-hero{padding:15px 36px;border:none;border-radius:99px;font-family:'Poppins',sans-serif;font-size:15px;font-weight:800;color:#fff;background:linear-gradient(135deg,#2563eb,#0ea5e9);cursor:pointer;transition:all .25s;box-shadow:0 8px 28px rgba(37,99,235,.38);}
.btn-hero:hover{transform:translateY(-3px);box-shadow:0 14px 38px rgba(37,99,235,.48);}
.btn-hero2{padding:14px 28px;border:2px solid rgba(37,99,235,.18);border-radius:99px;font-family:'Poppins',sans-serif;font-size:14px;font-weight:600;color:#2563eb;background:transparent;cursor:pointer;transition:all .25s;}
.btn-hero2:hover{background:#eff6ff;border-color:#2563eb;}

.hero-trust{display:flex;flex-wrap:wrap;gap:18px;font-size:12.5px;color:#94a3b8;font-weight:500;opacity:0;}
.ht{display:flex;align-items:center;gap:6px;}
.ht-ck{width:16px;height:16px;border-radius:50%;background:linear-gradient(135deg,#10b981,#34d399);display:flex;align-items:center;justify-content:center;font-size:8px;color:#fff;flex-shrink:0;}

/* ── HERO RIGHT — CARD STACK ── */
.hero-right{position:relative;z-index:2;display:flex;align-items:center;justify-content:center;}

.card-stack-wrap{
  position:relative;
  width:360px;height:440px;
  perspective:1400px;
  transform-style:preserve-3d;
  margin-bottom:96px; /* room for thumb strip + dots */
}

/* Dot grid of hoverable zones */
.hover-zones{
  position:absolute;inset:0;z-index:10;
  display:grid;grid-template-columns:repeat(5,1fr);grid-template-rows:repeat(5,1fr);
  gap:0;pointer-events:all;
}
.hz{cursor:none;} /* invisible hover zones */

/* actual card stack */
.card-stack{
  position:absolute;inset:0;
  transform-style:preserve-3d;
  transition:transform .5s cubic-bezier(.34,1.2,.64,1);
}
.sc{
  position:absolute;border-radius:22px;overflow:hidden;
  box-shadow:0 32px 64px rgba(0,0,0,.2);
  transition:transform .55s cubic-bezier(.34,1.2,.64,1), opacity .4s ease;
  will-change:transform;
}
.sc0{width:310px;height:390px;top:15px;left:10px;z-index:3;transform:rotateY(-8deg) rotateX(4deg) translateZ(0px);}
.sc1{width:285px;height:365px;top:28px;left:50px;z-index:2;opacity:.82;transform:rotateY(-4deg) rotateX(2deg) translateZ(-30px);}
.sc2{width:260px;height:340px;top:40px;left:85px;z-index:1;opacity:.58;transform:rotateY(2deg) rotateX(0deg) translateZ(-60px);}

/* hover state — spread cards */
.card-stack-wrap:hover .sc0{transform:rotateY(-14deg) rotateX(6deg) translateZ(22px) translateX(-12px);}
.card-stack-wrap:hover .sc1{transform:rotateY(-6deg) rotateX(3deg) translateZ(-8px) translateX(6px);}
.card-stack-wrap:hover .sc2{transform:rotateY(4deg) rotateX(1deg) translateZ(-40px) translateX(22px);}

.sc-img{
  width:100%;height:100%;object-fit:cover;display:block;
  transition:opacity .35s ease, transform .4s ease;
}
.sc-img.changing{opacity:0;transform:scale(1.05);}

.sc-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(15,23,42,.78) 0%,transparent 52%);padding:18px;display:flex;flex-direction:column;justify-content:flex-end;}
.sc-prompt-badge{font-size:10.5px;font-weight:600;color:rgba(255,255,255,.72);background:rgba(255,255,255,.12);backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,.16);border-radius:8px;padding:5px 10px;margin-bottom:8px;display:inline-flex;align-items:center;gap:5px;width:fit-content;}
.sc-lbl{font-size:14px;font-weight:700;color:#fff;line-height:1.4;}

/* floating badge */
.stack-badge{position:absolute;top:14px;right:8px;z-index:12;background:rgba(255,255,255,.95);backdrop-filter:blur(8px);border-radius:12px;padding:8px 12px;box-shadow:0 8px 24px rgba(0,0,0,.12);font-size:11px;font-weight:700;color:#2563eb;display:flex;align-items:center;gap:6px;animation:fbadge 3s ease-in-out infinite;}
@keyframes fbadge{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
.bd2{width:7px;height:7px;border-radius:50%;background:#10b981;}

/* thumbnail strip at bottom of stack */
.thumb-strip{
  position:absolute;bottom:-52px;left:0;right:0;
  display:flex;gap:8px;justify-content:center;z-index:5;
}
.thumb{
  width:36px;height:36px;border-radius:8px;overflow:hidden;
  border:2px solid transparent;cursor:pointer;
  transition:all .25s;opacity:.55;
  flex-shrink:0;
}
.thumb img{width:100%;height:100%;object-fit:cover;}
.thumb.active{border-color:#2563eb;opacity:1;transform:scale(1.1);box-shadow:0 4px 12px rgba(37,99,235,.35);}
.thumb:hover{opacity:.85;transform:scale(1.05);}

/* slide progress dots */
.slide-dots{position:absolute;bottom:-80px;left:0;right:0;display:flex;gap:8px;justify-content:center;z-index:5;}
.sdot{width:6px;height:6px;border-radius:99px;background:rgba(37,99,235,.2);transition:all .3s;cursor:pointer;}
.sdot.active{width:22px;background:#2563eb;}

/* ── MARQUEE ── */
.mq{overflow:hidden;padding:20px 0;background:#f8faff;border-top:1px solid rgba(37,99,235,.07);border-bottom:1px solid rgba(37,99,235,.07);}
.mq-t{display:flex;gap:12px;width:max-content;animation:mq 30s linear infinite;}
.mq-t2{animation-direction:reverse;animation-duration:24s;margin-top:10px;}
.mq-t:hover,.mq-t2:hover{animation-play-state:paused;}
@keyframes mq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.mq-img{width:176px;height:126px;border-radius:14px;flex-shrink:0;overflow:hidden;position:relative;cursor:pointer;transition:transform .3s;box-shadow:0 4px 14px rgba(0,0,0,.1);}
.mq-img:hover{transform:scale(1.06) rotate(-1.5deg);}
.mq-img img{width:100%;height:100%;object-fit:cover;display:block;}
.mq-lbl{position:absolute;bottom:0;left:0;right:0;background:linear-gradient(to top,rgba(15,23,42,.7),transparent);padding:10px 8px 6px;font-size:10px;font-weight:700;color:rgba(255,255,255,.85);}

/* ── SECTIONS ── */
.sec{padding:clamp(80px,10vw,130px) clamp(16px,5vw,72px);}
.sec-c{text-align:center;}
.stag{display:inline-block;font-size:10.5px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#2563eb;margin-bottom:14px;}
.sh2{font-size:clamp(30px,4vw,54px);font-weight:800;letter-spacing:-1.5px;color:#0f172a;margin-bottom:16px;line-height:1.1;}
.sh2 em{font-style:normal;color:#2563eb;}
.ssub{font-size:16px;color:#64748b;max-width:560px;margin:0 auto 60px;line-height:1.8;font-weight:400;}

/* HOW */
.hw-sec{background:linear-gradient(180deg,#f8faff 0%,#fff 100%);}
.hw-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;max-width:1080px;margin:0 auto;}
.hw-card{border-radius:24px;padding:44px 30px 36px;background:#fff;border:1.5px solid rgba(37,99,235,.09);position:relative;overflow:hidden;transform-style:preserve-3d;transition:all .4s cubic-bezier(.34,1.56,.64,1);opacity:0;transform:translateY(50px) rotateX(10deg);cursor:default;}
.hw-card:hover{border-color:rgba(37,99,235,.22);box-shadow:0 24px 56px rgba(37,99,235,.13);transform:translateY(-8px) rotateX(0deg) scale(1.01);}
.hw-n{font-size:80px;font-weight:900;line-height:.9;color:rgba(37,99,235,.06);margin-bottom:18px;letter-spacing:-4px;transition:color .3s;}
.hw-card:hover .hw-n{color:rgba(37,99,235,.13);}
.hw-icon{width:52px;height:52px;border-radius:16px;background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1px solid rgba(37,99,235,.12);display:flex;align-items:center;justify-content:center;font-size:24px;margin-bottom:20px;transition:transform .3s,box-shadow .3s;}
.hw-card:hover .hw-icon{transform:scale(1.1) translateY(-2px);box-shadow:0 8px 20px rgba(37,99,235,.15);}
.hw-title{font-size:18px;font-weight:800;color:#0f172a;margin-bottom:10px;letter-spacing:-.3px;}
.hw-desc{font-size:13.5px;color:#64748b;line-height:1.75;}
.hw-arr{position:absolute;right:-12px;top:50%;transform:translateY(-50%);font-size:20px;color:#dbeafe;font-weight:900;z-index:2;}
.hw-card:last-child .hw-arr{display:none;}

/* BEFORE/AFTER */
.ba-sec{background:#0f172a;padding:clamp(80px,10vw,130px) clamp(16px,5vw,72px);}
.ba-tag{color:rgba(255,255,255,.4);}
.ba-h2{color:#fff;}
.ba-h2 em{color:#60a5fa;}
.ba-sub{color:rgba(255,255,255,.45);}
.ba-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:28px;max-width:900px;margin:0 auto;}
.ba-card{border-radius:20px;overflow:hidden;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);transition:all .35s;opacity:0;transform:translateY(30px);}
.ba-card:hover{border-color:rgba(96,165,250,.3);transform:translateY(-5px);box-shadow:0 24px 48px rgba(0,0,0,.35);}
.ba-images{display:grid;grid-template-columns:1fr 1fr;gap:2px;}
.ba-side{position:relative;overflow:hidden;}
.ba-side img{width:100%;height:190px;object-fit:cover;display:block;transition:transform .4s;}
.ba-card:hover .ba-side img{transform:scale(1.05);}
.ba-badge{position:absolute;top:9px;left:9px;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.5px;padding:4px 9px;border-radius:99px;}
.bab{background:rgba(15,23,42,.7);color:rgba(255,255,255,.7);backdrop-filter:blur(4px);}
.baa{background:linear-gradient(135deg,#2563eb,#0ea5e9);color:#fff;}
.ba-bottom{padding:14px 16px;}
.ba-prompt{font-size:11.5px;color:rgba(255,255,255,.55);line-height:1.5;}
.ba-prompt b{color:rgba(255,255,255,.85);font-weight:600;}

/* SCROLLING TEXT */
.stxt{overflow:hidden;padding:50px 0;background:#f8faff;border-top:1px solid rgba(37,99,235,.06);border-bottom:1px solid rgba(37,99,235,.06);}
.stt{display:flex;gap:44px;width:max-content;animation:mq 20s linear infinite;}
.stt2{animation-direction:reverse;animation-duration:24s;margin-top:10px;}
.sti{font-size:clamp(36px,5vw,66px);font-weight:900;color:rgba(37,99,235,.07);white-space:nowrap;letter-spacing:-2px;cursor:default;transition:color .3s;}
.sti:hover{color:rgba(37,99,235,.16);}
.sti b{color:rgba(37,99,235,.15);}

/* BENTO */
.ft-sec{background:#f8faff;}
.bento{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;max-width:1180px;margin:0 auto;}
.bc{border-radius:22px;padding:36px 30px;background:#fff;border:1.5px solid rgba(37,99,235,.09);transition:all .38s cubic-bezier(.34,1.56,.64,1);opacity:0;transform:translateY(32px) rotateX(8deg);cursor:default;transform-style:preserve-3d;}
.bc:hover{border-color:rgba(37,99,235,.22);box-shadow:0 22px 48px rgba(37,99,235,.11);transform:translateY(-6px) rotateX(0deg) scale(1.01);}
.bc.span2{grid-column:span 2;background:linear-gradient(135deg,#eff6ff,#dbeafe);}
.bc.dark{background:linear-gradient(160deg,#0f172a,#1e3a8a);color:#fff;}
.bc-icon{width:50px;height:50px;border-radius:14px;background:linear-gradient(135deg,#2563eb,#0ea5e9);display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:18px;box-shadow:0 5px 16px rgba(37,99,235,.3);transition:transform .3s,box-shadow .3s;}
.bc:hover .bc-icon{transform:scale(1.12) translateZ(10px);box-shadow:0 9px 24px rgba(37,99,235,.4);}
.bc.dark .bc-icon{background:rgba(255,255,255,.12);box-shadow:none;}
.bc.span2 .bc-icon{background:linear-gradient(135deg,#2563eb,#0ea5e9);}
.bc-title{font-size:17px;font-weight:800;color:#0f172a;margin-bottom:9px;letter-spacing:-.3px;}
.bc.dark .bc-title{color:#fff;}
.bc.span2 .bc-title{color:#1e3a8a;}
.bc-desc{font-size:13.5px;color:#64748b;line-height:1.75;}
.bc.dark .bc-desc{color:rgba(255,255,255,.55);}
.bc.span2 .bc-desc{color:#3b82f6;}

/* GALLERY */
.gal-sec{background:#fff;overflow:hidden;}
.gal-rows{display:flex;flex-direction:column;gap:14px;margin-top:8px;}
.gal-row{display:flex;gap:14px;width:max-content;animation:mq 32s linear infinite;}
.gal-row.rev{animation-direction:reverse;animation-duration:28s;}
.gal-row:hover{animation-play-state:paused;}
.gc{width:240px;height:180px;border-radius:18px;flex-shrink:0;overflow:hidden;position:relative;cursor:pointer;transition:transform .4s cubic-bezier(.34,1.56,.64,1),box-shadow .3s;}
.gc:hover{transform:scale(1.07) translateY(-7px) rotate(.5deg);box-shadow:0 28px 56px rgba(0,0,0,.16);}
.gc img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .5s;}
.gc:hover img{transform:scale(1.1);}
.gc-ov{position:absolute;inset:0;background:linear-gradient(to top,rgba(15,23,42,.75) 0%,transparent 50%);padding:16px;display:flex;flex-direction:column;justify-content:flex-end;opacity:0;transition:opacity .3s;}
.gc:hover .gc-ov{opacity:1;}
.gc-lbl{font-size:13px;font-weight:700;color:#fff;margin-bottom:3px;}

/* TESTIMONIALS */
.testi-sec{background:#0f172a;padding:clamp(80px,10vw,130px) clamp(16px,5vw,72px);}
.testi-tag{color:rgba(255,255,255,.35);}
.testi-h2{color:#fff;}
.testi-h2 em{color:#60a5fa;}
.testi-sub{color:rgba(255,255,255,.45);}
.testi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px;max-width:1200px;margin:0 auto;}
.tc{padding:32px 26px;background:rgba(255,255,255,.05);border:1.5px solid rgba(255,255,255,.08);border-radius:20px;transition:all .35s;opacity:0;transform:translateY(30px);}
.tc:hover{background:rgba(255,255,255,.09);border-color:rgba(96,165,250,.3);transform:translateY(-5px);box-shadow:0 20px 44px rgba(0,0,0,.28);}
.tc-stars{display:flex;gap:3px;margin-bottom:18px;}
.tc-star{color:#f59e0b;font-size:14px;}
.tc-q{font-size:14px;color:rgba(255,255,255,.68);line-height:1.8;margin-bottom:24px;font-style:italic;}
.tc-foot{display:flex;align-items:center;gap:12px;}
.tc-av{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#0ea5e9);display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:800;color:#fff;flex-shrink:0;}
.tc-name{font-size:13px;font-weight:700;color:#fff;}
.tc-role{font-size:11px;color:rgba(255,255,255,.38);margin-top:2px;}

/* CTA */
.cta-sec{padding:clamp(110px,12vw,160px) clamp(16px,5vw,72px);background:linear-gradient(160deg,#0f172a 0%,#1e3a8a 55%,#1d4ed8 100%);text-align:center;position:relative;overflow:hidden;}
.cta-grid{position:absolute;inset:0;background-image:radial-gradient(circle,rgba(255,255,255,.04) 1px,transparent 1px);background-size:28px 28px;pointer-events:none;}
.cta-o1{position:absolute;width:700px;height:700px;border-radius:50%;background:radial-gradient(circle,rgba(99,102,241,.22),transparent 70%);top:-300px;right:-200px;pointer-events:none;}
.cta-o2{position:absolute;width:400px;height:400px;border-radius:50%;background:radial-gradient(circle,rgba(14,165,233,.16),transparent 70%);bottom:-160px;left:-100px;pointer-events:none;}
.cta-inner{position:relative;z-index:1;}
.cta-tag{color:rgba(255,255,255,.38);}
.cta-h2{font-size:clamp(36px,5.5vw,76px);font-weight:900;letter-spacing:-2.5px;color:#fff;margin-bottom:20px;line-height:1.04;opacity:0;transform:translateY(36px);}
.cta-h2 em{font-style:normal;color:#93c5fd;}
.cta-sub{font-size:17px;color:rgba(255,255,255,.5);max-width:500px;margin:0 auto 52px;line-height:1.8;opacity:0;}
.pricing-row{display:flex;align-items:stretch;justify-content:center;gap:14px;flex-wrap:wrap;margin-bottom:52px;opacity:0;}
.pr{padding:24px 20px;min-width:148px;background:rgba(255,255,255,.08);border:1.5px solid rgba(255,255,255,.12);border-radius:18px;text-align:center;backdrop-filter:blur(8px);transition:all .25s cubic-bezier(.34,1.56,.64,1);cursor:default;position:relative;}
.pr:hover{background:rgba(255,255,255,.14);border-color:rgba(255,255,255,.28);transform:translateY(-5px) scale(1.03);}
.pr.hot{border-color:rgba(96,165,250,.5);background:rgba(96,165,250,.1);}
.pr-hot{position:absolute;top:-11px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,#2563eb,#0ea5e9);color:#fff;font-size:9px;font-weight:800;padding:3px 10px;border-radius:99px;letter-spacing:.5px;text-transform:uppercase;white-space:nowrap;}
.pr-n{font-size:38px;font-weight:900;color:#fff;line-height:1;}
.pr-cr{font-size:9px;color:rgba(255,255,255,.38);text-transform:uppercase;letter-spacing:2px;margin:4px 0 8px;}
.pr-p{font-size:17px;font-weight:800;color:#60a5fa;}
.pr-l{font-size:9.5px;color:rgba(255,255,255,.28);margin-top:4px;}
.cta-btns{display:flex;align-items:center;gap:12px;justify-content:center;flex-wrap:wrap;opacity:0;}
.btn-cw{padding:17px 44px;border:none;border-radius:99px;font-family:'Poppins',sans-serif;font-size:16px;font-weight:900;color:#1d4ed8;background:#fff;cursor:pointer;transition:all .25s;box-shadow:0 8px 32px rgba(0,0,0,.18);}
.btn-cw:hover{transform:translateY(-3px);box-shadow:0 16px 44px rgba(0,0,0,.28);}
.btn-cg{padding:16px 36px;border:2px solid rgba(255,255,255,.22);border-radius:99px;font-family:'Poppins',sans-serif;font-size:14px;font-weight:600;color:#fff;background:transparent;cursor:pointer;transition:all .25s;}
.btn-cg:hover{border-color:rgba(255,255,255,.5);background:rgba(255,255,255,.07);}

/* FOOTER */
.footer{padding:52px clamp(16px,5vw,72px) 36px;background:#fff;border-top:1px solid rgba(37,99,235,.08);}
.ft-top{display:flex;align-items:flex-start;justify-content:space-between;flex-wrap:wrap;gap:32px;margin-bottom:44px;}
.ft-brand-col{}
.ft-brand{display:flex;align-items:center;gap:10px;margin-bottom:10px;}
.ft-name{font-size:19px;font-weight:800;color:#0f172a;letter-spacing:-.4px;}
.ft-name b{color:#2563eb;}
.ft-tagline{font-size:12.5px;color:#94a3b8;max-width:220px;line-height:1.6;}
.ft-parent{margin-top:12px;padding:10px 14px;background:#f8fafc;border:1px solid rgba(37,99,235,.08);border-radius:10px;display:inline-block;}
.ft-parent-txt{font-size:11.5px;color:#94a3b8;line-height:1.65;}
.ft-parent-txt b{color:#475569;font-weight:700;}
.ft-links-col{display:flex;flex-direction:column;gap:10px;}
.ft-col-t{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#0f172a;margin-bottom:4px;}
.ft-link{font-size:13px;color:#64748b;cursor:pointer;background:none;border:none;font-family:'Poppins',sans-serif;text-align:left;padding:0;transition:color .2s;}
.ft-link:hover{color:#2563eb;}
.ft-bottom{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:14px;padding-top:28px;border-top:1px solid rgba(37,99,235,.07);}
.ft-copy{font-size:12px;color:#cbd5e1;}
.ft-legal{display:flex;gap:20px;}

/* RESPONSIVE */
/* ── TABLET 960px ── */
/* ── TABLET 960px ── */
@media(max-width:960px){
  .hero{grid-template-columns:1fr;text-align:center;gap:24px;padding-top:90px;padding-bottom:60px;}
  .hero-tag,.hero-trust,.hero-btns{justify-content:center;}
  .hero-h1{text-align:center;min-height:auto;}
  .hero-sub{margin:0 auto 28px;}
  .hero-right{order:-1;}
  .card-stack-wrap{width:300px;height:360px;margin:0 auto 96px;}
  .sc0{width:268px;height:328px;top:12px;left:16px;}
  .sc1{width:248px;height:305px;top:24px;left:46px;}
  .sc2{width:228px;height:280px;top:36px;left:74px;}
  .hw-grid,.testi-grid{grid-template-columns:1fr;}
  .hw-arr{display:none;}
  .bento{grid-template-columns:1fr 1fr;}
  .bc.span2,.bc.dark{grid-column:span 1;}
  .ba-grid{grid-template-columns:1fr;}
  .ft-top{flex-direction:column;}
  .nav-links,.nav-r{display:none;}
  .nav-mob{display:flex;}
  .thumb-strip{bottom:-48px;}
  .slide-dots{bottom:-78px;}
  .ssub{font-size:14px;margin-bottom:44px;}
}

/* ── MOBILE 640px ── */
@media(max-width:640px){
  body{cursor:auto;}
  .cur,.cur-r{display:none;}
  .nav{height:58px;padding:0 16px;}
  .nav-name{font-size:17px;}
  .hero{
    grid-template-columns:1fr;
    display:flex;flex-direction:column;
    align-items:center;text-align:center;
    padding:76px 16px 48px;gap:0;
  }
  .hero-right{
    order:-1;width:100%;
    display:flex;justify-content:center;
    margin-bottom:112px;
  }
  .hero-left{
    width:100%;display:flex;
    flex-direction:column;align-items:center;
  }
  .hero-tag{display:none;}
  .hero-h1{font-size:clamp(28px,8vw,42px);letter-spacing:-1.5px;min-height:auto;text-align:center;margin-bottom:14px;}
  .hero-sub{font-size:13.5px;line-height:1.7;max-width:100%;margin:0 auto 24px;text-align:center;}
  .hero-btns{flex-direction:column;align-items:center;gap:10px;width:100%;margin-bottom:28px;}
  .btn-hero,.btn-hero2{width:100%;max-width:300px;text-align:center;justify-content:center;}
  .hero-trust{justify-content:center;gap:10px;font-size:11px;flex-wrap:wrap;}
  .card-stack-wrap{width:250px;height:290px;margin:0 auto;}
  .sc0{width:230px;height:278px;top:8px;left:10px;}
  .sc1{width:210px;height:256px;top:18px;left:36px;}
  .sc2{width:190px;height:234px;top:28px;left:58px;}
  .stack-badge{font-size:10px;padding:5px 9px;top:8px;right:4px;gap:5px;}
  .thumb-strip{gap:6px;bottom:-46px;}
  .thumb{width:28px;height:28px;border-radius:6px;}
  .slide-dots{bottom:-72px;}
  .sec{padding:56px 16px;}
  .stag{font-size:10px;letter-spacing:2px;}
  .sh2{font-size:clamp(22px,7vw,34px);letter-spacing:-1px;margin-bottom:12px;}
  .ssub{font-size:13px;margin-bottom:32px;max-width:100%;}
  .hw-card{padding:28px 20px 24px;}
  .hw-n{font-size:56px;}
  .hw-title{font-size:16px;}
  .hw-desc{font-size:13px;}
  .bento{grid-template-columns:1fr;gap:12px;}
  .bc{padding:26px 20px;}
  .bc-title{font-size:15px;}
  .bc-desc{font-size:13px;}
  .ba-sec{padding:56px 16px;}
  .ba-side img{height:140px;}
  .ba-prompt{font-size:11px;}
  .gc{width:150px;height:114px;}
  .gal-rows{gap:10px;}
  .mq-img{width:124px;height:92px;border-radius:10px;}
  .mq{padding:12px 0;}
  .stxt{padding:32px 0;}
  .sti{font-size:clamp(26px,7vw,46px);letter-spacing:-1.5px;}
  .testi-sec{padding:56px 16px;}
  .testi-grid{grid-template-columns:1fr;gap:12px;}
  .tc{padding:22px 18px;}
  .tc-q{font-size:13px;}
  .cta-sec{padding:72px 16px;}
  .cta-h2{font-size:clamp(26px,8vw,48px);letter-spacing:-1.5px;}
  .cta-sub{font-size:13.5px;}
  .pricing-row{gap:9px;}
  .pr{min-width:114px;padding:16px 12px;}
  .pr-n{font-size:28px;}
  .pr-p{font-size:13.5px;}
  .cta-btns{flex-direction:column;align-items:center;gap:10px;}
  .btn-cw,.btn-cg{width:100%;max-width:280px;}
  .footer{padding:36px 16px 24px;}
  .ft-top{gap:22px;}
  .ft-name{font-size:16px;}
  .ft-tagline{font-size:11px;}
  .ft-links-col{gap:7px;}
  .ft-col-t{font-size:10px;}
  .ft-link{font-size:12px;}
  .ft-bottom{flex-direction:column;align-items:flex-start;gap:8px;}
  .ft-copy{font-size:11px;}
}

/* ── SMALL MOBILE 380px ── */
@media(max-width:380px){
  .hero{padding:72px 12px 44px;}
  .hero-right{margin-bottom:104px;}
  .hero-h1{font-size:26px;letter-spacing:-1px;}
  .card-stack-wrap{width:210px;height:250px;}
  .sc0{width:192px;height:238px;top:6px;left:8px;}
  .sc1{width:174px;height:218px;top:16px;left:30px;}
  .sc2{width:156px;height:198px;top:26px;left:50px;}
  .thumb-strip{gap:5px;}
  .thumb{width:24px;height:24px;}
  .sh2{font-size:20px;}
  .pr{min-width:96px;padding:13px 9px;}
  .pr-n{font-size:24px;}
}
`;

export default function HomePage() {
  const nav = useNavigate();
  const [slideIdx, setSlideIdx] = useState(0);
  const [changing, setChanging] = useState(false);
  const autoRef   = useRef(null);
  const hoverRef  = useRef(null); // debounce
  const navRef    = useRef(null);
  const curRef    = useRef(null);
  const curRingRef= useRef(null);
  const hwRef     = useRef(null);
  const bentoRef  = useRef(null);
  const baRef     = useRef(null);
  const testiRef  = useRef(null);
  const ctaH2Ref  = useRef(null);
  const ctaSubRef = useRef(null);
  const priceRef  = useRef(null);
  const ctaBtnsRef= useRef(null);

  const slide = HERO_SLIDES[slideIdx];

  /* ── Change slide with crossfade ── */
  const changeSlide = useCallback((idx) => {
    if (idx === slideIdx) return;
    clearTimeout(autoRef.current);
    setChanging(true);
    setTimeout(() => {
      setSlideIdx(idx);
      setChanging(false);
      // restart auto-advance
      autoRef.current = setTimeout(() => {
        setSlideIdx(i => (i + 1) % HERO_SLIDES.length);
      }, 4000);
    }, 320);
  }, [slideIdx]);

  /* ── Auto advance every 4s ── */
  useEffect(() => {
    autoRef.current = setTimeout(() => {
      setSlideIdx(i => (i + 1) % HERO_SLIDES.length);
    }, 4000);
    return () => clearTimeout(autoRef.current);
  }, [slideIdx]);

  /* ── Hover zone handler (debounced 80ms) ── */
  const handleZoneHover = useCallback((zoneIdx) => {
    clearTimeout(hoverRef.current);
    hoverRef.current = setTimeout(() => {
      const target = zoneIdx % HERO_SLIDES.length;
      changeSlide(target);
    }, 80);
  }, [changeSlide]);

  /* ── Custom cursor ── */
  useEffect(() => {
    let raf, cx = window.innerWidth/2, cy = window.innerHeight/2, tx = cx, ty = cy;
    const mv = e => { tx = e.clientX; ty = e.clientY; };
    const tick = () => {
      cx += (tx-cx)*.12; cy += (ty-cy)*.12;
      if (curRef.current)     { curRef.current.style.left     = cx+"px"; curRef.current.style.top     = cy+"px"; }
      if (curRingRef.current) { curRingRef.current.style.left = cx+"px"; curRingRef.current.style.top = cy+"px"; }
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", mv);
    raf = requestAnimationFrame(tick);
    return () => { window.removeEventListener("mousemove", mv); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => {
    const on  = () => { curRef.current?.classList.add("big");    curRingRef.current?.classList.add("big"); };
    const off = () => { curRef.current?.classList.remove("big"); curRingRef.current?.classList.remove("big"); };
    const els = document.querySelectorAll("button,.nav-brand,.ft-link,.thumb,.sdot,.nl");
    els.forEach(el => { el.addEventListener("mouseenter",on); el.addEventListener("mouseleave",off); });
    return () => els.forEach(el => { el.removeEventListener("mouseenter",on); el.removeEventListener("mouseleave",off); });
  });

  /* ── GSAP ── */
  useEffect(() => {
    const load = () => new Promise(res => {
      if (window.gsap && window.ScrollTrigger) { res(); return; }
      const s1 = document.createElement("script");
      s1.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js";
      s1.onload = () => {
        const s2 = document.createElement("script");
        s2.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js";
        s2.onload = res;
        document.head.appendChild(s2);
      };
      document.head.appendChild(s1);
    });
    load().then(() => {
      const { gsap, ScrollTrigger } = window;
      gsap.registerPlugin(ScrollTrigger);

      /* hero entrance */
      const tl = gsap.timeline({ defaults:{ ease:"power4.out" } });
      tl.to(".hero-tag",   { opacity:1, x:0, duration:.7 })
        .to(".hero-h1",    { opacity:1, y:0, duration:.9 }, "-=.45")
        .to(".hero-sub",   { opacity:1, y:0, duration:.7 }, "-=.55")
        .to(".hero-btns",  { opacity:1, y:0, duration:.6 }, "-=.45")
        .to(".hero-trust", { opacity:1, duration:.5 }, "-=.3")
        .to(".card-stack-wrap", { opacity:1, y:0, duration:.9, ease:"back.out(1.2)" }, "-=.7");

      ScrollTrigger.create({
        start:"top -50",
        onEnter:    () => navRef.current?.classList.add("up"),
        onLeaveBack:() => navRef.current?.classList.remove("up"),
      });

      /* how-it-works 3D */
      hwRef.current?.querySelectorAll(".hw-card").forEach((el,i) => {
        gsap.to(el, { opacity:1, y:0, rotateX:0, duration:.8, delay:i*.15, ease:"back.out(1.4)", scrollTrigger:{ trigger:el, start:"top 87%" } });
      });
      bentoRef.current?.querySelectorAll(".bc").forEach((el,i) => {
        gsap.to(el, { opacity:1, y:0, rotateX:0, duration:.7, delay:(i%3)*.1, ease:"back.out(1.2)", scrollTrigger:{ trigger:el, start:"top 89%" } });
      });
      baRef.current?.querySelectorAll(".ba-card").forEach((el,i) => {
        gsap.to(el, { opacity:1, y:0, duration:.7, delay:i*.15, ease:"power2.out", scrollTrigger:{ trigger:el, start:"top 88%" } });
      });
      testiRef.current?.querySelectorAll(".tc").forEach((el,i) => {
        gsap.to(el, { opacity:1, y:0, duration:.65, delay:i*.12, ease:"power2.out", scrollTrigger:{ trigger:el, start:"top 88%" } });
      });
      gsap.to(ctaH2Ref.current,  { opacity:1, y:0, duration:.85, ease:"power3.out", scrollTrigger:{ trigger:ctaH2Ref.current, start:"top 80%" } });
      gsap.to(ctaSubRef.current,  { opacity:1, duration:.7, delay:.15, scrollTrigger:{ trigger:ctaSubRef.current, start:"top 82%" } });
      gsap.to(priceRef.current,   { opacity:1, duration:.7, delay:.22, scrollTrigger:{ trigger:priceRef.current, start:"top 82%" } });
      gsap.to(ctaBtnsRef.current, { opacity:1, duration:.6, delay:.3,  scrollTrigger:{ trigger:ctaBtnsRef.current, start:"top 85%" } });

      /* gallery parallax */
      document.querySelectorAll(".gal-row:not(.rev)").forEach(el => {
        gsap.to(el, { x:"-60px", ease:"none", scrollTrigger:{ trigger:".gal-sec", start:"top bottom", end:"bottom top", scrub:1.8 } });
      });
      document.querySelectorAll(".gal-row.rev").forEach(el => {
        gsap.to(el, { x:"60px", ease:"none", scrollTrigger:{ trigger:".gal-sec", start:"top bottom", end:"bottom top", scrub:1.8 } });
      });
      gsap.to(".stt2", { x:"-80px", ease:"none", scrollTrigger:{ trigger:".stxt", start:"top bottom", end:"bottom top", scrub:2 } });
      gsap.to(".hero-grid", { yPercent:-18, ease:"none", scrollTrigger:{ trigger:".hero", start:"top top", end:"bottom top", scrub:true } });

      return () => ScrollTrigger.getAll().forEach(t=>t.kill());
    });
  }, []);

  /* mouse tilt on hero cards */
  useEffect(() => {
    const stack = document.querySelector(".card-stack");
    const onMove = e => {
      if (!stack) return;
      const { innerWidth:w, innerHeight:h } = window;
      const x = (e.clientX/w - .5) * 16;
      const y = (e.clientY/h - .5) * 10;
      stack.style.transform = `rotateY(${-6 + x*.3}deg) rotateX(${3 - y*.2}deg)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior:"smooth" });
  const G1 = GALLERY, G2 = [...GALLERY].reverse(), G3 = [...GALLERY.slice(3),...GALLERY.slice(0,3)];

  return (
    <>
      <style>{CSS}</style>
      <style>{`:root{--accent:${slide.accent}}`}</style>

      {/* cursor */}
      <div className="cur" ref={curRef}/>
      <div className="cur-r" ref={curRingRef}/>

      {/* NAV */}
      <nav className="nav" ref={navRef}>
        <div className="nav-brand" onClick={() => window.scrollTo({top:0,behavior:"smooth"})}>
          <Logo size={26} color="#2563eb"/>
          <span className="nav-name">Pic<b>9</b>ine</span>
        </div>
        <div className="nav-links">
          {["features","how","gallery","pricing"].map(id=>(
            <button key={id} className="nl" onClick={()=>scrollTo(id)}>{id.charAt(0).toUpperCase()+id.slice(1)}</button>
          ))}
        </div>
        <div className="nav-r">
          <button className="btn-out" onClick={()=>nav("/login")}>Log in</button>
          <button className="btn-fill" onClick={()=>nav("/login")}>Get started →</button>
        </div>
        <div className="nav-mob">
          <button className="btn-out" onClick={()=>nav("/login")}>Login</button>
          <button className="btn-fill" onClick={()=>nav("/login")}>Start</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-grid"/>

        {/* LEFT — content changes on slide */}
        <div className="hero-left">
          <div className={`hero-tag ${changing?"txt-out":""}`}
            style={{opacity:0,transform:"translateX(-30px)",transition:"opacity .4s ease, transform .4s ease, color .3s"}}>
            <div className="tag-ic">✦</div>
            <div className="tag-dot"/>
            {slide.tag}
          </div>

          <h1 className="hero-h1" style={{opacity:0,transform:"translateY(40px)"}}>
            <span className={`h1-a ${changing?"txt-out":""}`}>{slide.h1a}</span>
            <span className={`h1-b ${changing?"txt-out":""}`}>{slide.h1b}</span>
            <span className={`h1-c ${changing?"txt-out":""}`}>{slide.h1c}</span>
          </h1>

          <p className={`hero-sub ${changing?"txt-out":""}`}
            style={{opacity:0,transform:"translateY(24px)",transition:"opacity .35s ease .05s, transform .35s ease .05s"}}>
            {slide.sub}
          </p>

          <div className="hero-btns" style={{opacity:0,transform:"translateY(20px)"}}>
            <button className="btn-hero" onClick={()=>nav("/login")}>Start for free →</button>
            <button className="btn-hero2" onClick={()=>scrollTo("how")}>See how it works</button>
          </div>

          <div className="hero-trust" style={{opacity:0}}>
            {["No credit card","4 free credits","HD 1024×1024","Instant results"].map((t,i)=>(
              <div key={i} className="ht"><div className="ht-ck">✓</div><span>{t}</span></div>
            ))}
          </div>

          {/* slide dots below text */}
          <div style={{display:"flex",gap:8,marginTop:28,opacity:0}} className="hero-trust">
            {HERO_SLIDES.map((_,i)=>(
              <button key={i}
                className={`sdot${i===slideIdx?" active":""}`}
                onClick={()=>changeSlide(i)}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — 3D card stack with hover zones */}
        <div className="hero-right">
          <div className="card-stack-wrap" style={{opacity:0,transform:"translateY(30px)"}}>

            {/* invisible hover zones layered on top */}
            <div className="hover-zones">
              {Array.from({length:25},(_,i)=>(
                <div key={i} className="hz"
                  onMouseEnter={()=>handleZoneHover(i)}
                />
              ))}
            </div>

            {/* floating AI badge */}
            <div className="stack-badge">
              <div className="bd2"/>
              AI Generated
            </div>

            {/* card stack */}
            <div className="card-stack">
              {/* back card */}
              <div className="sc sc2">
                <img
                  className={`sc-img ${changing?"changing":""}`}
                  src={slide.img2}
                  alt="ai"
                />
              </div>
              {/* mid card */}
              <div className="sc sc1">
                <img
                  className={`sc-img ${changing?"changing":""}`}
                  src={slide.img1}
                  alt="ai"
                />
              </div>
              {/* front card */}
              <div className="sc sc0">
                <img
                  className={`sc-img ${changing?"changing":""}`}
                  src={slide.img0}
                  alt="ai"
                />
                <div className="sc-overlay">
                  <div className={`sc-prompt-badge ${changing?"txt-out":""}`}>✦ Prompt used</div>
                  <div className={`sc-lbl ${changing?"txt-out":""}`}>{slide.prompt}</div>
                </div>
              </div>
            </div>

            {/* thumbnail strip */}
            <div className="thumb-strip">
              {HERO_SLIDES.map((s,i)=>(
                <div key={i}
                  className={`thumb${i===slideIdx?" active":""}`}
                  onMouseEnter={()=>changeSlide(i)}
                  onClick={()=>changeSlide(i)}
                >
                  <img src={s.img0} alt={s.tag} loading="lazy"/>
                </div>
              ))}
            </div>

            {/* progress dots */}
            <div className="slide-dots">
              {HERO_SLIDES.map((_,i)=>(
                <div key={i}
                  className={`sdot${i===slideIdx?" active":""}`}
                  onClick={()=>changeSlide(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="mq">
        <div className="mq-t">
          {[...GALLERY,...GALLERY].map((g,i)=>(
            <div key={i} className="mq-img"><img src={g.url} alt={g.label} loading="lazy"/><div className="mq-lbl">{g.label}</div></div>
          ))}
        </div>
        <div className="mq-t mq-t2">
          {[...GALLERY.slice(5),...GALLERY,...GALLERY.slice(0,5)].map((g,i)=>(
            <div key={i} className="mq-img" style={{opacity:.7}}><img src={g.url} alt={g.label} loading="lazy"/><div className="mq-lbl">{g.label}</div></div>
          ))}
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="sec sec-c hw-sec" id="how">
        <div className="stag">Simple process</div>
        <h2 className="sh2">Three steps to <em>perfection</em></h2>
        <p className="ssub">No design skills, no Photoshop, no learning curve. Just describe and generate.</p>
        <div className="hw-grid" ref={hwRef}>
          {STEPS.map((s,i)=>(
            <div key={i} className="hw-card">
              <div className="hw-n">{s.n}</div>
              <div className="hw-icon">{s.icon}</div>
              <div className="hw-title">{s.title}</div>
              <div className="hw-desc">{s.desc}</div>
              <div className="hw-arr">→</div>
            </div>
          ))}
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="ba-sec sec-c">
        <div className="stag ba-tag">Image to Image</div>
        <h2 className="sh2 ba-h2">Transform <em>any photo</em></h2>
        <p className="ssub ba-sub">Upload an image, describe the change. AI handles the rest — backgrounds, lighting, styling.</p>
        <div className="ba-grid" ref={baRef}>
          {BEFORE_AFTER.map((b,i)=>(
            <div key={i} className="ba-card">
              <div className="ba-images">
                <div className="ba-side"><img src={b.before} alt="before" loading="lazy"/><div className="ba-badge bab">Before</div></div>
                <div className="ba-side"><img src={b.after} alt="after" loading="lazy"/><div className="ba-badge baa">After</div></div>
              </div>
              <div className="ba-bottom"><div className="ba-prompt"><b>Prompt: </b>{b.prompt}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* SCROLLING TEXT */}
      <div className="stxt">
        <div className="stt">
          {["Generate","·","Transform","·","Create","·","Imagine","·","Design","·","Visualise","·",
            "Generate","·","Transform","·","Create","·","Imagine","·","Design","·","Visualise","·"].map((w,i)=>(
            <div key={i} className="sti">{w==="·"?<b>·</b>:w}</div>
          ))}
        </div>
        <div className="stt stt2" style={{marginTop:10}}>
          {["Portrait","·","Product","·","Fashion","·","Nature","·","Food","·","Editorial","·","Brand","·",
            "Portrait","·","Product","·","Fashion","·","Nature","·","Food","·","Editorial","·","Brand","·"].map((w,i)=>(
            <div key={i} className="sti">{w==="·"?<b>·</b>:w}</div>
          ))}
        </div>
      </div>

      {/* BENTO FEATURES */}
      <section className="sec sec-c ft-sec" id="features">
        <div className="stag">Everything you need</div>
        <h2 className="sh2">Built for <em>creators</em></h2>
        <p className="ssub">Every tool to generate, transform, and perfect your images.</p>
        <div className="bento" ref={bentoRef}>
          <div className="bc span2">
            <div className="bc-icon">⚡</div>
            <div className="bc-title">Generate in seconds</div>
            <div className="bc-desc">Nano Banana AI (Gemini 2.5 Flash) creates photorealistic images from plain text — no waiting, no queues. Just describe and get stunning results instantly.</div>
          </div>
          <div className="bc dark">
            <div className="bc-icon">🖼</div>
            <div className="bc-title">Image to Image</div>
            <div className="bc-desc">Upload any photo and transform it. Change colors, backgrounds, add models wearing your products — all from natural language.</div>
          </div>
          {FEATURES.slice(2).map((f,i)=>(
            <div key={i} className="bc">
              <div className="bc-icon">{f.icon}</div>
              <div className="bc-title">{f.title}</div>
              <div className="bc-desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="sec gal-sec" id="gallery">
        <div style={{textAlign:"center",marginBottom:48}}>
          <div className="stag">Gallery</div>
          <h2 className="sh2">See what's <em>possible</em></h2>
          <p className="ssub">Every style, every scene, every subject — all from a single prompt.</p>
        </div>
        <div className="gal-rows">
          {[G1,G2,G3].map((row,ri)=>(
            <div key={ri} className={`gal-row${ri%2===1?" rev":""}`}>
              {[...row,...row].map((g,i)=>(
                <div key={i} className="gc">
                  <img src={g.url} alt={g.label} loading="lazy"/>
                  <div className="gc-ov"><div className="gc-lbl">{g.label}</div></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testi-sec sec-c">
        <div className="stag testi-tag">Loved by creators</div>
        <h2 className="sh2 testi-h2">What our users <em>say</em></h2>
        <p className="ssub testi-sub">Thousands of creators, brands, and entrepreneurs use Pic9ine every day.</p>
        <div className="testi-grid" ref={testiRef}>
          {TESTIMONIALS.map((t,i)=>(
            <div key={i} className="tc">
              <div className="tc-stars">{Array(5).fill(0).map((_,j)=><span key={j} className="tc-star">★</span>)}</div>
              <div className="tc-q">"{t.q}"</div>
              <div className="tc-foot">
                <div className="tc-av">{t.av}</div>
                <div><div className="tc-name">{t.name}</div><div className="tc-role">{t.role}</div></div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-sec" id="pricing">
        <div className="cta-grid"/><div className="cta-o1"/><div className="cta-o2"/>
        <div className="cta-inner">
          <div className="stag cta-tag">Simple pricing</div>
          <h2 className="cta-h2" ref={ctaH2Ref}>Start free, scale<br/><em>as you create.</em></h2>
          <p className="cta-sub" ref={ctaSubRef}>Credits never expire. Get 4 free on signup — no card needed. Buy more whenever you're ready.</p>
          <div className="pricing-row" ref={priceRef}>
            {PACKS.map((pk,i)=>(
              <div key={i} className={`pr${pk.hot?" hot":""}`}>
                {pk.hot&&<div className="pr-hot">Most Popular</div>}
                <div className="pr-n">{pk.n}</div>
                <div className="pr-cr">Credits</div>
                <div className="pr-p">{pk.p}</div>
                <div className="pr-l">{pk.l}</div>
              </div>
            ))}
          </div>
          <div className="cta-btns" ref={ctaBtnsRef}>
            <button className="btn-cw" onClick={()=>nav("/login")}>Get started free →</button>
            <button className="btn-cg" onClick={()=>scrollTo("features")}>Learn more</button>
          </div>
          <div style={{marginTop:22,fontSize:12,color:"rgba(255,255,255,.25)"}}>4 free credits · No credit card · Instant access</div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="ft-top">
          <div className="ft-brand-col">
            <div className="ft-brand"><Logo size={22} color="#2563eb"/><span className="ft-name">Pic<b>9</b>ine</span></div>
            <div className="ft-tagline">AI Image Studio — Generate stunning, photorealistic images from text in seconds.</div>
            <div className="ft-parent">
              <div className="ft-parent-txt">
                A product by <b>seo9ine</b><br/>
                <span style={{fontSize:10.5,color:"#cbd5e1"}}>seo9ine is the parent company behind Pic9ine and other creative AI tools.</span>
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:52,flexWrap:"wrap"}}>
            <div className="ft-links-col">
              <div className="ft-col-t">Product</div>
              {["Features","How it works","Gallery","Pricing"].map(l=>(
                <button key={l} className="ft-link" onClick={()=>scrollTo(l.toLowerCase().replace(/ /g,""))}>{l}</button>
              ))}
            </div>
            <div className="ft-links-col">
              <div className="ft-col-t">Account</div>
              {["Sign in","Register","Dashboard"].map(l=>(
                <button key={l} className="ft-link" onClick={()=>nav("/login")}>{l}</button>
              ))}
            </div>
            <div className="ft-links-col">
              <div className="ft-col-t">Company</div>
              <button className="ft-link">About seo9ine</button>
              <button className="ft-link">Contact</button>
              <button className="ft-link">Privacy Policy</button>
            </div>
          </div>
        </div>
        <div className="ft-bottom">
          <div className="ft-copy">© 2026 Pic9ine · A <strong style={{color:"#64748b"}}>seo9ine</strong> product · All rights reserved</div>
          <div className="ft-legal">
            <button className="ft-link">Terms</button>
            <button className="ft-link">Privacy</button>
            <button className="ft-link">Cookies</button>
          </div>
        </div>
      </footer>
    </>
  );
}